// ============================================================
// DTOs compartidos entre apps/api y apps/web.
//
// Source-of-truth: docs/01-stack.md + docs/06-fase2-execution.md
// (derivados conceptualmente de los Mongoose schemas en
// apps/api/src/models/, sin dependencia runtime de mongoose).
//
// Convenciones:
//   - Campos siempre opcionales salvo el `_id` y los keys de la coleccion.
//   - Discriminadores via uniones literales donde aplica (TradeLogDoc).
//   - Las shapes "[unknown - needs other-app context]" del vault de v1
//     quedan como `Record<string, unknown>` (acceso safe pero sin
//     autocompletado).
// ============================================================

export type ObjectIdString = string;

// ---------- tradeLogs (3 shapes en una coleccion) ----------

export interface SessionStartConfig {
  symbol?: string;
  timeSpan?: string;
  offset?: number;
  atrPeriod?: number;
  boxSizeMultiplier?: number;
  minSameDirection?: number;
  minReversalBlocks?: number;
  reversalFilterAfterBlocks?: number;
  tradeOnVolume?: number;
  accountBalance?: number;
  riskPercent?: number;
  dollarsPerPoint?: number;
  marginPerContract?: number;
  allowMidCandleExit?: boolean;
  stopLossPoints?: number;
  takeProfitPoints?: number;
  trailActivatePoints?: number;
  trailingStopPercent?: number;
  useVolumeMin?: boolean;
  volumeMin?: number;
  tradovateAPI?: boolean;
  accountName?: string;
  debugMode?: boolean;
  tickDebug?: boolean;
  useCVDConfirmation?: boolean;
  alwaysExitOnReversal?: boolean;
  cvdVolumeEMAPeriod?: number;
  mode?: 'live' | 'paper' | 'sim';
  slippage?: number;
  developerMonitor?: boolean;
  filterWarmupToTradingHours?: boolean;
  warmupSessions?: number;
  enableBracketReset?: boolean;
  // Resto de flags de strategia: passthrough.
  [key: string]: unknown;
}

export interface SessionWarmupData {
  structured?: {
    vNextRestored?: boolean;
    sourceRenkoSingleId?: string;
    candleCount?: number;
    atrValue?: number;
    lastBoxPrice?: number;
    renkoBlocksFormed?: number;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface SessionStartDoc {
  _id?: ObjectIdString;
  sessionId: string;
  type: 'SESSION_START';
  symbol?: string;
  timestamp?: string; // ISO Date
  candleSessionId?: string;
  accountName?: string;
  customTitle?: string;
  operatorId?: string;
  dailyStartTime?: string;
  dailyEndTime?: string;
  developerMonitor?: boolean;
  configSource?: string | null;
  timeSlots?: unknown[] | null;
  useOrphanMode?: boolean;
  warmupData?: SessionWarmupData;
  config?: SessionStartConfig;
}

export interface SessionEndDoc {
  _id?: ObjectIdString;
  sessionId: string;
  type: 'SESSION_END';
  endTime?: string; // ISO
  timestamp?: string; // ISO
  performanceStats?: {
    totalTrades?: number;
    totalProfit?: number;
    winRate?: number;
    profitFactor?: number | string; // legacy: puede ser non-numeric en docs viejos
    maxDrawdown?: number;
    sessionDuration?: number; // ms
    [key: string]: unknown;
  };
  slotStats?: Record<string, { totalPnL?: number; [key: string]: unknown }>;
  slotTransitions?: unknown[];
}

export interface TradeDoc {
  _id?: ObjectIdString;
  sessionId: string;
  // type ausente == trade
  candleSessionId?: string;
  side?: 'long' | 'short';
  status?: 'open' | 'closed';
  entryTime?: string; // ISO
  entryPrice?: number;
  exitTime?: string;
  exitPrice?: number;
  quantity?: number;
  profit?: number; // canonico (server.js sessionSummaries)
  profitLoss?: number; // legacy (migration guide)
  outcome?: 'win' | 'loss';
  stopLoss?: number;
  takeProfit?: number;
  fillTime?: string;
  account?: string;
  orderId?: string;
}

export type TradeLogDoc = SessionStartDoc | SessionEndDoc | TradeDoc;

// ---------- sessionCandles ----------

export interface SessionCandleDoc {
  _id?: ObjectIdString;
  sessionId: string;
  symbol?: string;
  // string "YYYY-MM-DD HH:mm" (Eastern) en docs nuevos | Date en viejos.
  // Como string ISO al pasar por la API.
  time?: string;
  open?: number;
  high?: number;
  low?: number;
  close?: number;
  volume?: number;
  source?: string;
}

// ---------- renkoBlocks ----------

export interface RenkoBlockDoc {
  _id?: ObjectIdString;
  sessionId: string;
  symbol?: string;
  open?: number;
  close?: number;
  direction?: 'up' | 'down';
  boxSize?: number;
  startTime?: string;
  time?: string;
  candleClose?: number;
  candleHigh?: number;
  candleLow?: number;
  candleAtr?: number;
  volume?: number;
  index?: number;
  timestamp?: string;
  candleCount?: number;
}

// ---------- sweepResults ----------

export interface SweepResultDoc {
  _id?: ObjectIdString;
  sweepId?: string;
  runType?: 'single' | 'sweep';
  renkoSingleId?: string | null;
  params?: Record<string, unknown>;
  dateRange?: { from?: string; to?: string };
  topResults?: unknown[];
  weeklyBreakdown?: unknown[];
  symbol?: string;
  timeSpan?: string;
  operator?: string;
  winRate?: number;
  profitFactor?: number | string;
  totalPnL?: number;
  numTrades?: number;
}

// ---------- btSessionBlocks ----------

export interface BtSessionBlockDoc {
  _id?: ObjectIdString;
  renkoSingleId: string;
  index?: number;
  direction?: 'up' | 'down';
  open?: number;
  close?: number;
  boxSize?: number;
  time?: string;
  candleAtr?: number;
  blockType?: 'warmup' | 'trading';
}

// ---------- btTradeLogs ----------

export interface BtTradeLogDoc {
  _id?: ObjectIdString;
  renkoSingleId?: string;
  sessionId?: string;
  side?: 'long' | 'short';
  status?: string;
  entryTime?: string;
  entryPrice?: number;
  exitTime?: string;
  exitPrice?: number;
  quantity?: number;
  profit?: number;
  profitLoss?: number;
  // Otros campos del parity API: passthrough
  [key: string]: unknown;
}

// ---------- btSessionCandles ----------

export interface BtSessionCandleDoc {
  _id?: ObjectIdString;
  renkoSingleId?: string;
  symbol?: string;
  time?: string;
  open?: number;
  high?: number;
  low?: number;
  close?: number;
  volume?: number;
  [key: string]: unknown;
}

// ---------- warmupParityResults ----------

export type ParityStatus = 'OK' | 'DRIFT' | 'CRITICAL' | string;

export interface EngineStateAtLaunch {
  atr?: number;
  boxPrice?: number;
  blockCount?: number;
  boxSize?: number;
  [key: string]: unknown;
}

export interface WarmupParityResultDoc {
  _id?: ObjectIdString;
  sessionId: string;
  status?: ParityStatus | null;
  engineStateAtLaunch?: EngineStateAtLaunch;
  originSweepId?: string | null;
  // Resto de campos de la parity API: passthrough
  [key: string]: unknown;
}

// ---------- parityCheckResults ----------

export interface ParitySidePerformance {
  wins?: number;
  losses?: number;
  winRate?: number;
  profitFactor?: number | string;
  avgWin?: number;
  avgLoss?: number;
  [key: string]: unknown;
}

export interface ParitySide {
  blockCount?: number;
  tradeCount?: number;
  pnl?: number;
  performance?: ParitySidePerformance;
  [key: string]: unknown;
}

export interface ParityCheckResultDoc {
  _id?: ObjectIdString;
  sessionId: string;
  renkoSingleId?: string;
  status?: ParityStatus;
  blockCountDiff?: number;
  tradeCountDiff?: number;
  live?: ParitySide;
  bt?: ParitySide;
  performanceDiff?: Record<string, unknown>;
  [key: string]: unknown;
}

// ---------- simulatedTrades ----------

export type SimulatedTradeCloseReason =
  | 'block-reversal'
  | 'reversal-signal'
  | 'mid-candle-stop-loss'
  | 'mid-candle-take-profit'
  | 'mid-candle-trailing-stop'
  | 'end-of-day'
  | 'daily-hours-end'
  | 'session-end';

export interface SimulatedTradeDoc {
  _id?: ObjectIdString;
  sessionId: string;
  candleSessionId?: string;
  symbol?: string;
  source?: 'rebuildRenko';
  rebuildTimestamp?: string;
  orderId?: string;
  status?: 'closed';
  side?: 'long' | 'short';
  entryTime?: string;
  entryPrice?: number;
  exitTime?: string;
  exitPrice?: number;
  quantity?: number;
  profitLoss?: number;
  closeReason?: SimulatedTradeCloseReason;
  entryBlockIndex?: number;
  exitBlockIndex?: number;
  valuePerPoint?: number;
  exitConfig?: Record<string, unknown>;
}

// ---------- conflictAlerts ----------

export interface ConflictAlertDoc {
  _id?: ObjectIdString;
  blocker?: { sessionId?: string; [key: string]: unknown };
  blocked?: { sessionId?: string; [key: string]: unknown };
  timestamp?: string;
  [key: string]: unknown;
}

// ---------- sanityAlerts ----------

export interface SanityAlertDoc {
  _id?: ObjectIdString;
  mongoState?: {
    openTrades?: Array<{ sessionId?: string; [key: string]: unknown }>;
    [key: string]: unknown;
  };
  timestamp?: string;
  [key: string]: unknown;
}

// ============================================================
// API DTOs (responses) - el aggregation real vive en Fase 3+
// ============================================================

export type SessionStatus = 'live' | 'closed' | 'pending' | 'unknown';

export interface SessionSummary {
  sessionId: string;
  symbol: string;
  account?: string;
  status: SessionStatus;
  customTitle?: string;
  startTime?: string;
  endTime?: string;
  pnl?: number;
  tradesCount?: number;
  winRate?: number;
  developerMonitor?: boolean;
}

// ============================================================
// Live Monitor (Fase 4) - DTOs del endpoint
// GET /api/live-monitor-summary?mode=live|developer
// ============================================================

export type LiveMonitorMode = 'live' | 'developer';

export type AccountKind = 'live' | 'paper' | 'sim' | 'demo' | 'unknown';

export interface LiveOpenTrade {
  side: 'long' | 'short' | null;
  entryTime: string | null;
  entryPrice: number | null;
  quantity: number | null;
  stopLoss: number | null;
  takeProfit: number | null;
  orderId: string | null;
  // Sin default arbitrario (audit v1 §6 fix). Si el trade doc no lo trae,
  // queda null y el frontend muestra "N/A" en lugar de calcular Live P&L
  // como si fuera NQ ($20/pt).
  valuePerPoint: number | null;
}

export interface LiveSparklinePoint {
  exitTime: string;
  cumulativePnL: number;
}

export interface LiveClosedTrade {
  side: 'long' | 'short' | null;
  entryTime: string | null;
  exitTime: string | null;
  // entryPrice / exitPrice: fill real de Tradovate (avgFillPrice). Lo que
  // realmente cobramos/pagamos. Acentua en la UI como "Tradovate Entry/Exit".
  entryPrice: number | null;
  exitPrice: number | null;
  // entryTriggerPrice / exitTriggerPrice: precio teorico que el engine usó
  // como senial (close de vela o Renko block close). Lo que el backtest
  // habria visto. ~22% de trades legacy no tienen estos campos -> null.
  entryTriggerPrice: number | null;
  exitTriggerPrice: number | null;
  quantity: number | null;
  // pnl REAL: lo que el bot calculo con los FILLS de Tradovate (incluye
  // slippage). Se lee directo del campo `profitLoss` del doc Mongo, no se
  // recalcula. Este es el numero principal del dashboard.
  pnl: number;
  // btPnl: P&L "modelo" calculado con los triggers del engine (sin slippage).
  // Formula: (exitTriggerPrice - entryTriggerPrice) * sign * qty * vpp.
  // Fallback al pnl real para trades legacy sin triggers (~22%).
  // Solo se muestra como columna informativa en el Trade List para comparar
  // modelo vs realidad. Helper: apps/api/src/lib/pipelines/btPnL.ts.
  btPnl: number;
  closeReason: string | null;
}

export interface LiveSessionCard {
  sessionId: string;
  candleSessionId: string;
  symbol: string;
  accountName: string | null;
  customTitle: string | null;
  operatorId: string;
  startTime: string | null;
  dailyStartTime: string | null;
  dailyEndTime: string | null;
  developerMonitor: boolean;
  configSource: string | null;
  timeSlots: unknown[] | null;
  config: Record<string, unknown> | null;
  vNextRestored: boolean;

  // Stats (mismos campos que SessionSummary del service de sessions + drawdown).
  tradeCount: number;
  // pnl REAL (lo que el bot calculo con fills de Tradovate, incluye slippage).
  totalPnL: number;
  // pnl BT (sin slippage, trigger prices) — informativo, se muestra stacked
  // bajo el totalPnL real en las cards.
  totalBtPnL: number;
  wins: number;
  winRate: number;
  winAmount: number;
  lossAmount: number;
  profitFactor: number | null;
  maxDrawdown: number; // siempre <= 0
  openPositions: number;

  // Mode badges (heredados de docs/sessions-tab-badges.md de v1).
  isTimeSlotSession: boolean;
  filterWarmupToTradingHours: boolean;
  dwpmActive: boolean;
  dwpmLabel: string | null;
  warmupSessions: number;
  enableBracketReset: boolean;
  // PX5 badge (cyan-500 + Zap + pulse): config.exitMode === 'polling'.
  // Indica que el bot pidió velas cada 5s mientras hay trade abierto para
  // detectar stop-loss/trailing intra-candle. Trades con esta config tienen
  // closeReason: 'polling-mid-candle-*' cuando salen intra-candle.
  pollingExit: boolean;
  accountKind: AccountKind;

  // Especifico Live Monitor.
  openTrades: LiveOpenTrade[];
  // Ultimos N (default 30) closed trades como puntos para sparkline equity curve.
  // El frontend renderea SVG inline, no Plotly (mitigacion del riesgo Fase 4
  // documentado en docs/02-plan.md: N mini-plotlys pega con muchas sessions).
  recentClosedPnL: LiveSparklinePoint[];
  // Closed trades del session - para Trade List del body expandido.
  // Sorted desc por exitTime (mas recientes arriba). Limit ~50 por session
  // para no inflar payload (sessions tipicas tienen <50 closed trades).
  closedTrades: LiveClosedTrade[];
}

export interface LiveMonitorAggregate {
  activeSessionsCount: number;
  totalPnL: number;
  // Stacked bajo el totalPnL en el Aggregate Bar.
  totalBtPnL: number;
  totalTrades: number;
  totalWins: number;
  totalLosses: number;
  totalOpenPositions: number;
  totalWinAmount: number;
  totalLossAmount: number;
  winRate: number; // combinado: totalWins / totalTrades * 100
  profitFactor: number | null; // combinado
}

export interface LiveMonitorSummaryResponse {
  mode: LiveMonitorMode;
  sessions: LiveSessionCard[];
  aggregate: LiveMonitorAggregate;
  // ISO timestamp del servidor (frontend lo usa para "Last updated: Xs ago").
  fetchedAt: string;
}

// ============================================================
// Parity check DTOs (Fase 6).
//
// Shape verificado contra Pepeflow real (smoke test 2026-05-14). Difiere
// del audit en varios campos: agregamos `score`/`scoreBreakdown`/`slippage`
// que el upstream devuelve y el audit no documentaba.
//
// Source-of-truth: docs/parity-flow-audit.md + smoke test apps/api/scripts/
// smoke-parity.mjs.
// ============================================================

// ParityStatus ya esta definido arriba (Fase 2 stub) como union string.
// Lo reusamos. Aca solo agregamos los tipos especificos de Fase 6.
export type ParityInterval = '1h' | '2h' | '4h' | '6h' | '12h' | 'off';

/**
 * Performance breakdown que aparece dentro de live.performance / bt.performance.
 * Todos opcionales porque el upstream puede omitir campos en edge cases.
 */
export interface ParityPerformance {
  totalTrades?: number;
  wins?: number;
  losses?: number;
  winRate?: number; // 0..100
  profitFactor?: number;
  avgWin?: number;
  avgLoss?: number;
  largestWin?: number;
  largestLoss?: number;
}

export interface ParitySideStats {
  blockCount: number;
  tradeCount: number;
  pnl: number;
  performance: ParityPerformance;
}

export interface ParityScoreBreakdown {
  trades?: Record<string, unknown>;
  blocks?: Record<string, unknown>;
  pnl?: Record<string, unknown>;
}

export interface ParityPerformanceDiff {
  winsDiff?: number;
  lossesDiff?: number;
  winRateDiff?: number;
  profitFactorDiff?: number;
  avgWinDiff?: number;
  avgLossDiff?: number;
}

/**
 * Polling-exit model gap — clasificacion explicativa del upstream cuando parte
 * del pnlDiff proviene de stops que el Live cerro por ticks intra-vela
 * (polling-watcher) y el BT, que solo replaya velas de 1m, no puede reproducir.
 * Es una brecha de arquitectura conocida, NO un bug. El upstream deja
 * `status` en CRITICAL a proposito; estos campos solo agregan contexto.
 *
 * Todos los campos son opcionales: ausentes en parity checks normales
 * (solo aparecen cuando el upstream detecta polling-exits irreproducibles).
 *
 * IMPORTANTE (semantica que la UI NO debe malinterpretar):
 *   - livePollingStopLossPnl = lo que el Live perdio en esos trades. NO es
 *     "cuanto explica la brecha". El upstream deliberadamente NO expone un
 *     monto causal porque no es defendible (attributionReliable: false).
 *   - rawPnlDiffNetOfSlippage puede ser null (paper/sim sin slippage real).
 */
export interface PollingExitModelGapStats {
  /** Cantidad de stop-loss que el Live cerro via polling-watcher (tick intra-vela). */
  livePollingStopLossCount: number;
  /** PnL que el Live realizo en esos polling-stops. NO es el monto causal de la brecha. */
  livePollingStopLossPnl: number;
  /** pnlDiff crudo (Live - BT), igual a ParityVerdict.pnlDiff. */
  rawPnlDiff: number;
  /** pnlDiff neto de slippage. null en paper/sim (sin slippage real). */
  rawPnlDiffNetOfSlippage: number | null;
  /**
   * false cuando el upstream no puede atribuir un monto causal confiable a la
   * brecha. Cuando es false, la UI solo debe mostrar hechos duros — nunca
   * inventar ni mostrar un "monto causal de la brecha".
   */
  attributionReliable: boolean;
}

export interface ParityVerdict {
  score: number;
  scoreBreakdown: ParityScoreBreakdown;
  blocksMatch: boolean;
  tradesMatch: boolean;
  blockCountDiff: number;
  tradeCountDiff: number;
  pnlDiff: number;
  status: ParityStatus;
  performanceDiff: ParityPerformanceDiff;
  // ----- Polling-exit model gap (opcionales, solo presentes cuando aplica) -----
  /** true cuando parte del pnlDiff viene de polling-exits irreproducibles por el BT. */
  pollingExitModelGap?: boolean;
  /** Explicacion larga de por que hay brecha (texto del upstream, listo para mostrar). */
  pollingExitModelGapReason?: string;
  /** Clasificacion machine-readable. Actualmente: 'POLLING_EXIT_MODEL_GAP'. */
  displayStatus?: string;
  /** Mensaje corto explicativo (una linea) para mostrar junto al CRITICAL. */
  displayMessage?: string;
  /** Hechos duros del gap. Ver PollingExitModelGapStats para la semantica. */
  pollingExitModelGapStats?: PollingExitModelGapStats;
}

/**
 * Slippage stats — feature nuevo del upstream no documentado en el audit
 * original. Pepeflow lo devuelve en cada parity-check.
 */
export interface ParitySlippage {
  tradesWithSlippage: number;
  totalEntrySlippagePts: number;
  totalExitSlippagePts: number;
  totalSlippagePts: number;
  totalSlippageCost: number;
  pnlDiffNetOfSlippage: number;
}

export interface ParityCheckResult {
  sessionId: string;
  symbol: string;
  live: ParitySideStats;
  bt: ParitySideStats;
  parity: ParityVerdict;
  // slippage es opcional por compat con upstreams viejos que aun no lo emiten.
  slippage?: ParitySlippage;
  // ISO timestamp del momento en que el upstream termino el check.
  timestamp: string;
}

/**
 * Tipo de cada item del array `blocks.results` / `trades.results` que devuelve
 * el endpoint de details. El audit listaba 4 valores; el upstream real puede
 * traer adicionales (MISSING_IN_BT / MISSING_IN_LIVE existen, mas otros).
 */
export type ParityDiffType = 'MATCH' | 'MISMATCH' | 'MISSING_IN_BT' | 'MISSING_IN_LIVE' | string;

export interface ParityBlockDiff {
  type: ParityDiffType;
  time?: string;
  // biome-ignore lint/suspicious/noExplicitAny: shape del block varia
  live?: any;
  // biome-ignore lint/suspicious/noExplicitAny: idem
  bt?: any;
  [key: string]: unknown;
}

export interface ParityTradeDiff {
  type: ParityDiffType;
  tradeIndex?: number;
  liveIndex?: number;
  btIndex?: number;
  // biome-ignore lint/suspicious/noExplicitAny: trade shape variable
  live?: any;
  // biome-ignore lint/suspicious/noExplicitAny: idem
  bt?: any;
  details?: {
    entryTimeMatch?: boolean;
    directionMatch?: boolean;
    exitTimeMatch?: boolean;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface ParityCheckDetails {
  sessionId: string;
  // renkoSingleId del backtest que se uso (no en audit, en respuesta real).
  renkoSingleId?: string;
  sessionStartTime?: string;
  blocks: {
    liveBlockCount: number;
    btBlockCount: number;
    matchCount: number;
    differenceCount: number;
    realDifferenceCount?: number;
    blocksMatch: boolean;
    results: ParityBlockDiff[];
  };
  trades: {
    liveTradeCount: number;
    btTradeCount: number;
    liveClosedCount?: number;
    btClosedCount?: number;
    matchCount: number;
    differenceCount: number;
    tradesMatch: boolean;
    livePnL: number;
    btPnL: number;
    pnlDiff: number;
    pairedCount?: number;
    liveOnlyCount?: number;
    btOnlyCount?: number;
    results: ParityTradeDiff[];
  };
}

/**
 * Respuesta del GET /api/parity/:id/alert. interval puede ser:
 *   - null   -> nunca se seteo alerta (default cuando no hay record)
 *   - 'off'  -> seteada y luego apagada explicitamente
 *   - '1h'.. -> activa con ese intervalo
 *
 * El audit decia que `interval` venia como 'off' o un string; el upstream
 * real puede devolver null. La UI debe manejar los 3 casos.
 */
export interface ParityAlertResponse {
  sessionId: string;
  interval: ParityInterval | null;
  active: boolean;
}

export interface ParityHealthResponse {
  available: boolean;
  url: string | null;
  server: 'local' | 'bigtester' | 'liltester' | 'pepeflow' | 'none';
  mode: 'local' | 'production';
  checkedAt: number;
}

// ============================================================
// Warmup parity DTOs.
// ============================================================

export interface WarmupEngineMetric {
  atr: number | null;
  boxPrice: number | null;
  blockCount: number | null;
  boxSize: number | null;
}

export interface WarmupEngineState {
  origin: WarmupEngineMetric | null;
  preflight: WarmupEngineMetric | null;
  originUnavailableReason: string | null;
}

/**
 * Shape del documento crudo de warmupParityResults (Mongo). Pasamos al
 * cliente con strict:false porque otras apps escriben campos adicionales
 * que el dashboard puede mostrar tal cual.
 */
export interface WarmupParityRawDoc {
  sessionId: string;
  status: ParityStatus | string;
  comparisonMode?: 'snapshot' | 'preflight';
  fallbackReason?: 'cutoff_in_no_trading_window' | 'legacy_no_snapshot' | string;
  origin?: Record<string, unknown>;
  snapshot?: Record<string, unknown>;
  preflight?: Record<string, unknown>;
  preflightFull?: Record<string, unknown>;
  diffs?: Record<string, unknown>;
  originSweepId?: string | null;
  engineStateAtLaunch?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface WarmupParityEnriched {
  result: WarmupParityRawDoc;
  engineState: WarmupEngineState;
}

/**
 * Tolerancias usadas para clasificar pass/fail por metrica en el modal de
 * Warmup Parity. Heredadas del audit v1 (TOLERANCES en línea 11033 de
 * rabbithole-dashboard.html). Si en el futuro el backend las devuelve en
 * cada response, la UI las consume desde ahi (BUG #1 audit).
 */
export const DEFAULT_PARITY_TOLERANCES = {
  totalPnL: 2000, // USD
  numTrades: 2,
  wins: 2,
  losses: 2,
  winRate: 0.015, // fraccion 0..1 (1.5%)
  profitFactor: 0.15,
} as const;

export type ParityToleranceMetric = keyof typeof DEFAULT_PARITY_TOLERANCES;
